import { API_BASES, API_PATHS } from "./apiPaths";
import { terminateSession } from "../utils/sessionManager";

const pendingRequestControllers = new Set();
let sessionValidationPromise = null;
let sessionRefreshPromise = null;

const isAbsoluteUrl = (url = "") => /^https?:\/\//i.test(url);

const buildUrl = (url, baseURL = API_BASES.app) => {
  if (!url) return baseURL || "";
  if (isAbsoluteUrl(url)) {
    try {
      const targetOrigin = new URL(url).origin;
      const apiOrigin = new URL(baseURL || API_BASES.app).origin;
      if (targetOrigin !== apiOrigin) {
        throw new Error("Blocked request to an untrusted API origin.");
      }
      return url;
    } catch (error) {
      if (error?.message === "Blocked request to an untrusted API origin.") throw error;
      throw new Error("Invalid API URL.");
    }
  }
  return `${baseURL}${url}`;
};

const normalizeHeaders = (headers = {}) => {
  const nextHeaders = {};
  Object.entries(headers).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      nextHeaders[key] = value;
    }
  });
  return nextHeaders;
};

const hasHeader = (headers, headerName) =>
  Object.keys(headers || {}).some((key) => key.toLowerCase() === headerName.toLowerCase());

export const getCookie = (name) => {
  if (typeof document === "undefined") return "";
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1] || "";
};

export const csrfHeader = () => {
  const token = getCookie("XSRF-TOKEN");
  return token ? { "X-XSRF-TOKEN": decodeURIComponent(token) } : {};
};

let csrfInitialisationPromise = null;

export const initialiseCsrf = ({ force = false } = {}) => {
  // Avoid an extra network round trip before every mutation once the server
  // has already issued a CSRF cookie for this browser session.
  if (!force && getCookie("XSRF-TOKEN")) return Promise.resolve();
  if (csrfInitialisationPromise) return csrfInitialisationPromise;

  csrfInitialisationPromise = sendRequest(API_PATHS.auth.csrf, {
    method: "GET",
    withCredentials: true,
  }, { allowRefresh: false, skipAuth: true }).finally(() => {
    csrfInitialisationPromise = null;
  });

  return csrfInitialisationPromise;
};

const headersToObject = (headers) => {
  const nextHeaders = {};
  headers.forEach((value, key) => {
    nextHeaders[key] = value;
  });
  return nextHeaders;
};

const serializeBody = (data, headers) => {
  if (data === undefined || data === null) {
    return undefined;
  }

  if (
    typeof data === "string"
    || data instanceof FormData
    || data instanceof Blob
    || data instanceof URLSearchParams
  ) {
    return data;
  }

  if (!hasHeader(headers, "Content-Type")) {
    headers["Content-Type"] = "application/json";
  }

  return JSON.stringify(data);
};

const parseResponseBody = async (response, responseType) => {
  if (response.status === 204) {
    return null;
  }

  if (responseType === "blob" && response.ok) {
    return response.blob();
  }

  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  try {
    return await response.text();
  } catch {
    return null;
  }
};

const createResponseObject = (response, data, url) => ({
  data,
  status: response.status,
  headers: headersToObject(response.headers),
  ok: response.ok,
  url,
});

const createHttpError = (responseObject, fallbackMessage) => {
  const responseData = responseObject?.data;
  const message =
    responseData?.message
    || responseData?.error
    || fallbackMessage
    || `Request failed with status ${responseObject?.status || "unknown"}.`;

  const error = new Error(message);
  error.response = responseObject;
  error.status = responseObject?.status;
  return error;
};

const createNetworkError = (originalError) => {
  const error = new Error(originalError?.message || "Network Error");
  error.cause = originalError;
  return error;
};

const isAuthRequestUrl = (requestUrl = "") => {
  const normalizedUrl = requestUrl.toLowerCase();

  return (
    normalizedUrl.includes("/v2/auth/")
  );
};

export const isSessionProbeUrl = (requestUrl = "") => {
  const normalizedUrl = requestUrl.toLowerCase();
  return normalizedUrl.includes("/v2/auth/session")
    || normalizedUrl.includes("/v2/auth/presence/heartbeat")
    || normalizedUrl.includes("/v2/auth/refresh")
    || normalizedUrl.includes("/v2/auth/csrf");
};

const hasStoredAuthMetadata = () => {
  try {
    return Boolean(window.localStorage.getItem("user") || window.sessionStorage.getItem("user"));
  } catch {
    return false;
  }
};

export const canRefreshRequest = (requestUrl = "") => {
  const normalizedUrl = requestUrl.toLowerCase();
  return !normalizedUrl.includes("/v2/auth/login")
    && !normalizedUrl.includes("/v2/auth/logout")
    && !normalizedUrl.includes("/v2/auth/refresh")
    && !normalizedUrl.includes("/v2/auth/csrf")
    && !normalizedUrl.includes("/v2/auth/forgot-password")
    && !normalizedUrl.includes("/v2/auth/reset-password");
};

const refreshSessionOnce = async () => {
  if (sessionRefreshPromise) return sessionRefreshPromise;
  sessionRefreshPromise = sendRequest(API_PATHS.auth.refresh, {
    method: "POST",
    withCredentials: true,
  }, { allowRefresh: false, skipAuth: true })
    .then((response) => {
      if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("auth:session-refreshed"));
      return response;
    })
    .finally(() => {
      sessionRefreshPromise = null;
    });
  return sessionRefreshPromise;
};

// A 401 from a business endpoint is not always a lost browser session. For
// example, an endpoint may have a backend permission/configuration issue while
// the user's cookie session remains valid. Verify the session independently
// before clearing local auth state.
const verifyActiveSession = async () => {
  if (sessionValidationPromise) return sessionValidationPromise;

  sessionValidationPromise = (async () => {
    try {
      const response = await fetch(buildUrl(API_PATHS.auth.session), {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        const payload = await parseResponseBody(response);
        const session = payload?.data ?? payload ?? {};
        return Boolean(session?.role) && payload?.success !== false;
      }
      if (response.status === 401 || response.status === 403) return false;
      return null;
    } catch {
      // A temporary network failure must not log a user out.
      return null;
    } finally {
      sessionValidationPromise = null;
    }
  })();

  return sessionValidationPromise;
};

const sendRequest = async (url, config = {}, meta = {}) => {
  const { allowRefresh = true, skipAuth = config.skipAuth === true } = meta;
  const headers = normalizeHeaders(config.headers);
  const requestUrl = buildUrl(url, config.baseURL);
  const method = (config.method || "GET").toUpperCase();
  const isAuthRequest = isAuthRequestUrl(requestUrl);
  const isUnsafeRequest = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
  // Public guest mutations (waiting room, join, attendance and media token)
  // must not inherit authenticated-workspace restrictions from stale browser
  // state. Their authorization is supplied by the admission/poll token and is
  // enforced by the backend endpoint itself.
  if (isUnsafeRequest && !skipAuth && typeof window !== "undefined") {
    let readOnly = false;
    try {
      readOnly = window.localStorage.getItem("subscriptionReadOnly") === "true";
    } catch {
      // Server authorization remains authoritative when browser storage is unavailable.
    }
    const normalizedUrl = requestUrl.toLowerCase();
    const isReadOnlyExemption = normalizedUrl.includes("/v2/auth/")
      || normalizedUrl.includes("/subscription/pay")
      || normalizedUrl.includes("/subscription/verify")
      || normalizedUrl.includes("/subscription/status");
    if (readOnly && !isReadOnlyExemption) {
      const error = new Error("This workspace is read-only while the school subscription is expired.");
      error.code = "SUBSCRIPTION_READ_ONLY";
      error.status = 402;
      throw error;
    }
  }
  if (isUnsafeRequest && !hasHeader(headers, "X-XSRF-TOKEN")) {
    // Unsafe requests must never race the CSRF bootstrap. This is particularly
    // important for background heartbeat/usage writes immediately after login.
    if (!getCookie("XSRF-TOKEN")) await initialiseCsrf();
    Object.assign(headers, csrfHeader());
  }

  let response;
  const controller = !config.signal && typeof AbortController !== "undefined"
    ? new AbortController()
    : null;
  if (controller) pendingRequestControllers.add(controller);

  try {
    response = await fetch(requestUrl, {
      method,
      headers,
      body: serializeBody(config.data, headers),
      // The refresh token must be an HttpOnly cookie. Sending credentials by
      // default is required when the API is hosted on a separate origin.
      credentials: config.withCredentials === false ? "same-origin" : "include",
      cache: config.cache,
      keepalive: config.keepalive || config.keepAlive || false,
      signal: config.signal || controller?.signal,
    });
  } catch (error) {
    throw createNetworkError(error);
  } finally {
    if (controller) pendingRequestControllers.delete(controller);
  }

  const data = await parseResponseBody(response, config.responseType);
  const responseObject = createResponseObject(response, data, requestUrl);
  if (response.ok) {
    if (isUnsafeRequest && typeof window !== "undefined") {
      const quotaManagedResource = /(?:\/api\/admin\/(?:students|teachers|classes|subjects|timetable|exams|fees|parents|users|notifications|import)|\/students\/(?:createStudent|import))(?:\/|$|\?)/i;
      if (quotaManagedResource.test(requestUrl)) {
        window.dispatchEvent(new CustomEvent("tenant-quota:changed"));
      }
    }
    return responseObject;
  }

  if (response.status === 401 && !skipAuth && allowRefresh && hasStoredAuthMetadata() && canRefreshRequest(requestUrl)) {
    try {
      await refreshSessionOnce();
      return sendRequest(url, config, { allowRefresh: false, skipAuth });
    } catch {
      // Fall through to authoritative session verification and logout only
      // when the refresh-token flow cannot restore the browser session.
    }
  }

  if ([401, 403].includes(response.status) && !skipAuth && (!isAuthRequest || isSessionProbeUrl(requestUrl))) {
    const isSessionEndpoint = requestUrl.toLowerCase().includes("/v2/auth/session");
    const hasActiveSession = isSessionEndpoint ? false : await verifyActiveSession();
    if (hasActiveSession === false && hasStoredAuthMetadata()) {
      terminateSession({
        reason: "expired",
        message: "Your session has expired. Please login again.",
      });
    }
  }

  // Global subscription expiry interception.
  // Skip for: super admins, and the subscription/payment endpoints themselves
  // (those are the renewal endpoints — dispatching here would cause a redirect
  // loop if the backend mistakenly blocks them with the subscription filter).
  // Also skip login endpoint because Login component handles 402 SUBSCRIPTION_EXPIRED specifically.
  if (data?.errorCode === "SUBSCRIPTION_EXPIRED" || responseObject?.status === 402) {
    const _path = requestUrl.toLowerCase();
    const _isRenewalEndpoint =
      _path.includes("/subscription/pay") ||
      _path.includes("/subscription/status") ||
      _path.includes("/subscription/plans") ||
      _path.includes("/subscription/request-extension") ||
      _path.includes("/subscription/verify") ||
      _path.includes("/payments/verify");
    const _isLoginEndpoint = _path.includes("/v2/auth/login");
    let _isSuperAdmin = false;
    let _isSchoolAdmin = false;
    try {
      const _raw = localStorage.getItem("user");
      const _parsed = JSON.parse(_raw);
      const _role = String(_parsed?.role ?? _parsed ?? "").toUpperCase().trim();
      _isSuperAdmin = _role === "ROLE_SUPER_ADMIN" || _role === "SUPER_ADMIN";
      _isSchoolAdmin = _role === "ROLE_ADMIN" || _role === "ROLE_SCHOOL";
    } catch { /* ignore parse errors */ }

    if (!_isSuperAdmin && !_isRenewalEndpoint && !_isLoginEndpoint) {
      if (_isSchoolAdmin) {
        // Expired school administrators remain signed in and are NOT routed away from school data.
        // We set subscriptionReadOnly so write requests are prevented, while read views remain open.
        try {
          window.localStorage.setItem("subscriptionReadOnly", "true");
        } catch { /* ignore */ }
      } else {
        window.dispatchEvent(new CustomEvent("subscription:expired"));
      }
    }
  }

  if (data?.errorCode === "FEATURE_DISABLED" && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("tenant-feature:disabled"));
  }

  throw createHttpError(responseObject);
};

export const requestSessionRefresh = async () => refreshSessionOnce();

// Kept as a no-op compatibility export for older callers. Refreshing a
// session must never require or retain a user's password in the browser.
export const setRefreshCredentials = () => {};

export const clearRefreshCredentials = () => {
  // Refresh credentials are held by the server in an HttpOnly cookie.
};

if (typeof window !== "undefined") {
  window.addEventListener("auth:logout", () => {
    pendingRequestControllers.forEach((controller) => controller.abort());
    pendingRequestControllers.clear();
    clearRefreshCredentials();
  });
}

const api = {
  request(config = {}) {
    return sendRequest(config.url || "", config);
  },

  get(url, config = {}) {
    return sendRequest(url, { ...config, method: "GET" });
  },

  post(url, data, config = {}) {
    return sendRequest(url, { ...config, method: "POST", data });
  },

  put(url, data, config = {}) {
    return sendRequest(url, { ...config, method: "PUT", data });
  },

  patch(url, data, config = {}) {
    return sendRequest(url, { ...config, method: "PATCH", data });
  },

  delete(url, config = {}) {
    return sendRequest(url, { ...config, method: "DELETE" });
  },
};

export default api;
